import { useEffect, useState } from "react";
import { sponsorshipAPI, equipmentAPI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Award, Loader2, Send, CheckCircle2, Clock, XCircle,
  Gamepad2, ArrowRight, ArrowLeft, Check, Plus, X, Camera,
  Info, Upload, FileText, Download, Trophy,
} from "lucide-react";
import SponsorPortfolio from "@/components/SponsorPortfolio";
import PageTitle from "@/components/PageTitle";

const HERO_IMG =
  "https://images.unsplash.com/photo-1541446804404-9ab0f7dc12ef?q=80&w=1800&auto=format&fit=crop";

const PLATFORMS = [
  { id: "iracing", label: "iRacing" },
  { id: "acc", label: "ACC" },
  { id: "lmu", label: "LMU" },
  { id: "ac", label: "Assetto Corsa" },
  { id: "ams2", label: "AMS2" },
  { id: "raceroom", label: "RaceRoom" },
  { id: "f1", label: "F1" },
  { id: "gt7", label: "GT7" },
  { id: "other", label: "Other" },
];

const SOCIAL_PLATFORMS = ["Twitch", "YouTube", "Facebook", "Instagram", "TikTok", "X/Twitter"];
const PROPOSAL_LABELS = ["Proposal", "Analytics", "Supporting docs"];
const MAX_PROPOSAL_FILES = 3;
// Images + common sim-racing / TradingPaints livery file types
const LIVERY_ACCEPT =
  "image/png,image/jpeg,image/webp,image/gif,image/bmp,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tga,.dds,.mip,.paint,.zip,.rar";
const PROPOSAL_ACCEPT =
  "application/pdf,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*";

const isImageData = (data) => typeof data === "string" && data.startsWith("data:image/");

const STEPS = [
  { id: "platform", title: "Platform", subtitle: "Select your sim" },
  { id: "details", title: "Details", subtitle: "Your info" },
  { id: "livery", title: "Livery", subtitle: "Required" },
  { id: "review", title: "Submit", subtitle: "Review & send" },
];

const STATUS = {
  pending: { label: "Pending review", icon: Clock, color: "hsl(45 100% 55%)" },
  deal_offered: { label: "Deal offered", icon: Award, color: "hsl(145 70% 50%)" },
  approved: { label: "Approved", icon: CheckCircle2, color: "hsl(145 70% 50%)" },
  rejected: { label: "Rejected", icon: XCircle, color: "hsl(2 100% 55%)" },
};

const TIER_LABELS = { grassroots: "Grassroots", rising: "Rising", pro: "Pro", academy: "SRC Academy" };

function DealOffer({ request, onAccepted }) {
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const deal = request.deal || {};
  const brands = deal.brands || [];

  const accept = async () => {
    if (!accepted) { toast.error("Please accept the Sponsorship Terms to continue."); return; }
    setBusy(true);
    try {
      await sponsorshipAPI.acceptDeal(request.id);
      toast.success("Sponsorship deal accepted — welcome aboard!");
      onAccepted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not accept the deal");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 rounded-lg border border-primary/50 bg-primary/5 p-4" data-testid="sponsor-deal-offer">
      <div className="flex items-center gap-2">
        <Award className="h-5 w-5 text-primary" />
        <p className="font-display text-lg font-semibold">You&apos;ve received a sponsorship offer!</p>
      </div>
      <div className="flex items-center gap-2">
        <Badge className="border-0 bg-primary/20 text-primary">{TIER_LABELS[deal.tier] || "Grassroots"} tier</Badge>
      </div>
      {deal.details && (
        <div className="whitespace-pre-wrap rounded-md border border-border bg-secondary/40 p-3 text-sm" data-testid="sponsor-deal-details">{deal.details}</div>
      )}
      {brands.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground">Included brands</p>
          <div className="mt-2 flex flex-wrap gap-3">
            {brands.map((b) => (
              <div key={b.id} className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1 text-sm">
                {b.logo_url && <img src={b.logo_url} alt="" className="h-6 w-6 rounded object-contain" />}
                {b.name}
              </div>
            ))}
          </div>
        </div>
      )}
      <label className="flex items-start gap-2 text-sm" data-testid="sponsor-deal-terms-checkbox">
        <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 h-4 w-4 accent-[hsl(2_100%_44%)]" />
        <span>
          I accept the{" "}
          <a href="/sponsorship-terms" target="_blank" rel="noreferrer" className="text-primary underline" data-testid="sponsor-deal-terms-link">Sponsorship Terms</a>.
          By accepting, I agree that the sponsorship agreement includes these Terms and the details provided by SRC above.
        </span>
      </label>
      <Button onClick={accept} disabled={busy || !accepted} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sponsor-deal-accept">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept sponsorship deal"}
      </Button>
    </div>
  );
}

const emptyPlatformData = {
  licenseLevel: "",
  username: "",
  profileScreenshot: "",
  accountLink: "",
  wins: [{ name: "", position: "" }],
  tournaments: "",
  liveryImage: "",
  liveryFileName: "",
  liveryDescription: "",
};

const RIG_FIELDS = [
  { key: "wheel_base", label: "Wheel Base", category: "wheel_base", required: true },
  { key: "steering_wheel", label: "Steering Wheel", category: "steering_wheel", required: true },
  { key: "pedals", label: "Pedals", category: "pedals", required: true },
  { key: "shifter", label: "Shifter", category: "shifter" },
  { key: "handbrake", label: "Handbrake", category: "handbrake" },
  { key: "cockpit", label: "Cockpit / Stand", category: "cockpit" },
  { key: "motion_system", label: "Motion System", category: "motion_system" },
];

const emptyRigEquipment = {
  wheel_base: "", steering_wheel: "", pedals: "",
  shifter: "", handbrake: "", cockpit: "", motion_system: "",
};

const emptyForm = {
  selectedPlatforms: [],
  otherPlatform: "",
  firstName: "",
  lastName: "",
  socialLinks: [
    { platform: "Twitch", handle: "" },
    { platform: "YouTube", handle: "" },
    { platform: "Instagram", handle: "" },
  ],
  proposalFiles: [],
  additionalInfo: "",
  rigEquipment: { ...emptyRigEquipment },
  platformData: {},
};

// Common rig field: pick a brand (or "Other") + model/details, stored as one string.
function RigField({ label, category, manufacturers, value, onChange, optional }) {
  const brands = (manufacturers?.[category] || []).filter((b) => b.name !== "Other");
  const matched = brands.find((b) => value && value.toLowerCase().startsWith(b.name.toLowerCase()));
  const isOther = !matched && !!value;
  const brandValue = matched ? matched.name : (isOther ? "__other__" : "");
  const modelValue = matched ? value.slice(matched.name.length).trim() : (isOther ? value : "");

  const setBrand = (v) => {
    if (v === "__other__") onChange(modelValue || "");
    else onChange(modelValue ? `${v} ${modelValue}` : v);
  };
  const setModel = (text) => {
    if (brandValue === "__other__" || brandValue === "") onChange(text);
    else onChange(`${matched.name} ${text}`.trim());
  };

  return (
    <div className="space-y-1.5">
      <Label>{label}{optional && <span className="font-normal text-muted-foreground"> (optional)</span>}</Label>
      <div className="flex gap-2">
        <Select value={brandValue} onValueChange={setBrand}>
          <SelectTrigger className="h-10 w-[150px] shrink-0 bg-background text-sm" data-testid={`sponsor-rig-${category}-brand`}>
            <SelectValue placeholder="Brand" />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(240_8%_10%)] text-white border-[hsl(240_6%_17%)]">
            {brands.map((b) => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)}
            <SelectItem value="__other__">Other</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={modelValue}
          onChange={(e) => setModel(e.target.value)}
          placeholder={brandValue === "__other__" ? "Enter brand & model" : "Model / details"}
          data-testid={`sponsor-rig-${category}-model`}
        />
      </div>
    </div>
  );
}

function ProgressBar({ currentStep }) {
  return (
    <div className="mb-6">
      <div className="mb-2 flex items-start justify-between">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex flex-1 flex-col items-center">
            <div
              className={`flex h-7 w-7 items-center justify-center rounded-full border-2 text-xs font-bold ${
                i <= currentStep
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground"
              }`}
            >
              {i < currentStep ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span className={`mt-1 text-[11px] font-medium ${i <= currentStep ? "text-foreground" : "text-muted-foreground"}`}>
              {s.title}
            </span>
          </div>
        ))}
      </div>
      <div className="h-[3px] w-full overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${(currentStep / (STEPS.length - 1)) * 100}%` }}
        />
      </div>
    </div>
  );
}

function ApplicationsPanel({ requests, loading, active, onOpen }) {
  return (
    <Card className="border-border bg-card p-6">
      <h2 className="mb-4 font-display text-xl font-semibold">Your applications</h2>
      {loading ? (
        <div className="space-y-2">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
      ) : requests.length === 0 ? (
        <p className="text-sm text-muted-foreground">No applications yet — submit one to get started.</p>
      ) : (
        <div className="space-y-3">
          {requests.map((r) => {
            const s = STATUS[r.status] || STATUS.pending;
            const Icon = s.icon;
            return (
              <button
                key={r.id}
                onClick={() => onOpen(r)}
                className={`w-full rounded-md border p-3 text-left transition-colors ${
                  active?.id === r.id ? "border-primary bg-secondary/50" : "border-border bg-card hover:border-primary/40"
                }`}
                data-testid={`sponsor-app-${r.id}`}
              >
                <div className="flex items-center justify-between">
                  <p className="font-semibold">{r.real_name}</p>
                  <Badge className="gap-1 border-0 text-[10px] font-semibold text-background" style={{ backgroundColor: s.color }}>
                    <Icon className="h-3 w-3" /> {s.label}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{r.platform} · License {r.license_level}</p>
                {(r.status === "approved" || r.status === "active") && (
                  <p className="mt-1 text-[10px] uppercase tracking-wider text-primary" data-testid={`sponsor-list-tier-${r.id}`}>
                    Tier: {(r.tier || "grassroots").charAt(0).toUpperCase() + (r.tier || "grassroots").slice(1)}
                    {r.has_pending_updates && <span className="ml-2 text-amber-400">· Update pending review</span>}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default function Sponsorship() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [manufacturers, setManufacturers] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");

  const load = () => {
    sponsorshipAPI
      .getRequests()
      .then((r) => setRequests(Array.isArray(r.data) ? r.data : r.data?.requests || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => load(), []);

  // Load equipment manufacturers for the rig dropdowns, and prefill the rig
  // from the driver's saved profile gear (editable before submit).
  useEffect(() => {
    equipmentAPI.getManufacturers().then((r) => setManufacturers(r.data || null)).catch(() => setManufacturers(null));
  }, []);
  useEffect(() => {
    if (!user?.equipment) return;
    setForm((f) => ({
      ...f,
      rigEquipment: { ...emptyRigEquipment, ...Object.fromEntries(
        RIG_FIELDS.map((rf) => [rf.key, user.equipment?.[rf.key] || ""])
      ) },
    }));
  }, [user]);

  // Auto-open the conversation for the user's active/approved (or pending) application
  // so the messaging portal is immediately visible instead of requiring a click.
  useEffect(() => {
    if (active || requests.length === 0) return;
    const target =
      requests.find((r) => r.status === "approved" || r.status === "active") ||
      requests.find((r) => r.status === "pending");
    if (target) openThread(target);
  }, [requests]);

  const hasActive = requests.some((r) => ["pending", "deal_offered", "approved", "active"].includes(r.status));
  const approvedRequest = requests.find((r) => r.status === "approved" || r.status === "active");
  const pendingRequest = requests.find((r) => r.status === "pending");
  const dealRequest = requests.find((r) => r.status === "deal_offered");
  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const addSocialLink = () => update({ socialLinks: [...form.socialLinks, { platform: "Twitch", handle: "" }].slice(0, 8) });
  const removeSocialLink = (i) => update({ socialLinks: form.socialLinks.filter((_, idx) => idx !== i) });
  const setSocialLink = (i, patch) => {
    const next = [...form.socialLinks];
    next[i] = { ...next[i], ...patch };
    update({ socialLinks: next });
  };

  const pickProposal = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const remaining = MAX_PROPOSAL_FILES - form.proposalFiles.length;
    if (remaining <= 0) {
      toast.error(`You can upload up to ${MAX_PROPOSAL_FILES} files.`);
      return;
    }
    const toAdd = files.slice(0, remaining);
    if (files.length > remaining) toast.error(`Only ${MAX_PROPOSAL_FILES} files allowed — extra files were skipped.`);
    Promise.all(
      toAdd.map(
        (f, idx) =>
          new Promise((res) => {
            const r = new FileReader();
            r.onload = (ev) =>
              res({ label: PROPOSAL_LABELS[(form.proposalFiles.length + idx) % PROPOSAL_LABELS.length], fileName: f.name, data: ev.target.result });
            r.readAsDataURL(f);
          })
      )
    ).then((added) => update({ proposalFiles: [...form.proposalFiles, ...added] }));
  };
  const setProposalLabel = (i, label) => {
    const next = [...form.proposalFiles];
    next[i] = { ...next[i], label };
    update({ proposalFiles: next });
  };
  const removeProposal = (i) => update({ proposalFiles: form.proposalFiles.filter((_, idx) => idx !== i) });

  const addWin = (platformId) => {
    const d = form.platformData[platformId] || emptyPlatformData;
    updatePlatformData(platformId, { wins: [...(d.wins || []), { name: "", position: "" }] });
  };
  const setWin = (platformId, idx, patch) => {
    const d = form.platformData[platformId] || emptyPlatformData;
    const wins = [...(d.wins || [])];
    wins[idx] = { ...wins[idx], ...patch };
    updatePlatformData(platformId, { wins });
  };
  const removeWin = (platformId, idx) => {
    const d = form.platformData[platformId] || emptyPlatformData;
    updatePlatformData(platformId, { wins: (d.wins || []).filter((_, x) => x !== idx) });
  };

  const pickLivery = (platformId) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => updatePlatformData(platformId, { liveryImage: ev.target.result, liveryFileName: file.name });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const pickScreenshot = (platformId) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => updatePlatformData(platformId, { profileScreenshot: ev.target.result });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const platformLabel = (id) => {
    if (id === "other") return form.otherPlatform.trim() || "Other";
    return PLATFORMS.find((p) => p.id === id)?.label || id;
  };

  const togglePlatform = (id) => {
    setForm((f) => {
      const selected = f.selectedPlatforms.includes(id);
      const selectedPlatforms = selected
        ? f.selectedPlatforms.filter((x) => x !== id)
        : [...f.selectedPlatforms, id];
      const platformData = { ...f.platformData };
      if (!selected && !platformData[id]) platformData[id] = { ...emptyPlatformData };
      return { ...f, selectedPlatforms, platformData };
    });
  };

  const updatePlatformData = (id, patch) => {
    setForm((f) => ({
      ...f,
      platformData: { ...f.platformData, [id]: { ...(f.platformData[id] || emptyPlatformData), ...patch } },
    }));
  };

  const next = () => {
    if (step === 0) {
      if (form.selectedPlatforms.length === 0) return toast.error("Select at least one racing platform.");
      if (form.selectedPlatforms.includes("other") && !form.otherPlatform.trim())
        return toast.error("Please enter your 'Other' platform name.");
    }
    if (step === 1) {
      if (!form.firstName.trim()) return toast.error("Please enter your first name.");
      if (!form.lastName.trim()) return toast.error("Please enter your last name.");
      if (form.socialLinks.every((l) => !l.handle.trim())) return toast.error("Add at least one social or profile link.");
      if (!form.rigEquipment.wheel_base?.trim()) return toast.error("Select your wheel base.");
      if (!form.rigEquipment.steering_wheel?.trim()) return toast.error("Select your steering wheel.");
      if (!form.rigEquipment.pedals?.trim()) return toast.error("Select your pedals.");
      for (const id of form.selectedPlatforms) {
        const d = form.platformData[id] || {};
        if (!d.licenseLevel?.trim()) return toast.error(`Enter skill / safety rating for ${platformLabel(id)}.`);
        if (!d.username?.trim()) return toast.error(`Enter your username / profile name for ${platformLabel(id)}.`);
        if (!d.profileScreenshot) return toast.error(`Upload a driver profile screenshot for ${platformLabel(id)}.`);
        if (!d.tournaments?.trim()) return toast.error(`Enter upcoming tournaments / leagues for ${platformLabel(id)}.`);
      }
    }
    if (step === 2) {
      for (const id of form.selectedPlatforms) {
        const d = form.platformData[id] || {};
        if (!d.liveryImage && !d.liveryDescription?.trim())
          return toast.error(`Upload a livery image or description for ${platformLabel(id)}.`);
      }
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const validLinks = form.socialLinks
        .filter((l) => l.handle.trim())
        .map((l) => ({ platform: l.platform, handle: l.handle.trim() }));
      const platforms = form.selectedPlatforms.map((id) => {
        const d = form.platformData[id] || {};
        const wins = (d.wins || [])
          .filter((w) => (w.name || "").trim() || (w.position || "").trim())
          .map((w) => ({ name: (w.name || "").trim(), position: (w.position || "").trim() }));
        return {
          platform: platformLabel(id),
          license_level: d.licenseLevel?.trim() || "Not specified",
          username: d.username?.trim() || "",
          profile_screenshot: d.profileScreenshot || "",
          account_link: d.accountLink?.trim() || "",
          livery_image: d.liveryImage || "",
          livery_file_name: d.liveryFileName || "",
          livery_description: d.liveryDescription?.trim() || "",
          wins,
          podiums_wins: wins.map((w) => (w.position ? `${w.name} - ${w.position}` : w.name)).filter(Boolean).join("; "),
          tournaments: d.tournaments?.trim() || "",
        };
      });
      await sponsorshipAPI.createRequest({
        real_name: `${form.firstName.trim()} ${form.lastName.trim()}`.trim(),
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim(),
        platforms,
        social_links: validLinks,
        proposal_files: form.proposalFiles.map((f) => ({ label: f.label, file_name: f.fileName, data: f.data })),
        additional_info: form.additionalInfo.trim(),
        rig_equipment: form.rigEquipment,
      });
      setSubmitted(true);
      setForm(emptyForm);
      setStep(0);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const openThread = async (r) => {
    setActive(r);
    setMessages([]);
    try {
      const res = await sponsorshipAPI.getMessages(r.id);
      setMessages(res.data?.messages || []);
    } catch {
      setMessages([]);
    }
  };
  const sendMsg = async () => {
    if (!msg.trim() || !active) return;
    try {
      await sponsorshipAPI.sendMessage(active.id, msg);
      setMsg("");
      const res = await sponsorshipAPI.getMessages(active.id);
      setMessages(res.data?.messages || []);
    } catch {
      toast.error("Could not send message");
    }
  };

  return (
    <div className="space-y-8" data-testid="sponsorship-page">
      <PageTitle
        title="Sponsorship Portal"
        subtitle="From early backing to getting paid to race to finding your way into a real car, apply for sponsorship to get started."
      />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        {/* Wizard */}
        <Card className="border-border bg-card p-6" data-testid="sponsor-wizard">
          <div className="mb-4 flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            <h2 className="font-display text-xl font-semibold">Apply for Sponsorship</h2>
          </div>

          {submitted ? (
            <div className="flex flex-col items-center py-8 text-center" data-testid="sponsor-success">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full" style={{ background: "hsla(145,70%,50%,0.15)" }}>
                <Check className="h-8 w-8 text-[hsl(145,70%,50%)]" />
              </div>
              <h3 className="font-display text-lg font-bold uppercase tracking-wider">Application submitted</h3>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                We&apos;ll review your application and get back to you within 48 hours. Track its status on the right.
              </p>
              <Button className="mt-6 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setSubmitted(false)} data-testid="sponsor-success-close">
                Done
              </Button>
            </div>
          ) : hasActive ? (
            <div className="space-y-3">
              {dealRequest && (
                <DealOffer request={dealRequest} onAccepted={load} />
              )}
              {pendingRequest && (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-300" data-testid="sponsor-pending-state">
                  <p className="flex items-center gap-2 font-semibold"><Clock className="h-4 w-4" /> Application pending review</p>
                  <p className="mt-1 text-xs text-amber-300/80">
                    We&apos;ll review your application within 48 hours. Track its status on the right and continue any conversation below.
                  </p>
                </div>
              )}
              {approvedRequest && (
                <SponsorPortfolio request={approvedRequest} onChanged={load} />
              )}
              {!approvedRequest && !pendingRequest && (
                <div className="rounded-md border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
                  You already have an active application. Track its status on the right and continue any conversation below.
                </div>
              )}
            </div>
          ) : (
            <>
              <ProgressBar currentStep={step} />

              {/* Step 0 — Platform (multi-select) */}
              {step === 0 && (
                <div className="space-y-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider">Select your platform(s)</p>
                    <p className="text-sm text-muted-foreground">Choose every sim racing platform you want sponsorship for</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {PLATFORMS.map((p) => {
                      const selected = form.selectedPlatforms.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => togglePlatform(p.id)}
                          className={`relative flex flex-col items-center gap-2 rounded-md border p-4 transition-colors ${
                            selected ? "border-primary bg-primary/10" : "border-border bg-secondary/50 hover:border-primary/40"
                          }`}
                          data-testid={`sponsor-platform-${p.id}`}
                        >
                          <div className={`flex h-10 w-10 items-center justify-center rounded-full ${selected ? "bg-primary/20" : "bg-card"}`}>
                            <Gamepad2 className={`h-5 w-5 ${selected ? "text-primary" : "text-muted-foreground"}`} />
                          </div>
                          <span className={`text-sm font-medium ${selected ? "text-foreground" : "text-muted-foreground"}`}>{p.label}</span>
                          {selected && (
                            <div className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary">
                              <Check className="h-3 w-3 text-primary-foreground" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {form.selectedPlatforms.length > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid="sponsor-selected-count">
                      {form.selectedPlatforms.length} platform{form.selectedPlatforms.length === 1 ? "" : "s"} selected
                    </p>
                  )}
                  {form.selectedPlatforms.includes("other") && (
                    <div className="space-y-1.5">
                      <Label>Specify the sim title(s) or platform(s) where you race*</Label>
                      <Input value={form.otherPlatform} onChange={(e) => update({ otherPlatform: e.target.value })} placeholder="Enter your racing platform" data-testid="sponsor-other-platform" />
                    </div>
                  )}
                </div>
              )}

              {/* Step 1 — Details (common + per-platform) */}
              {step === 1 && (
                <div className="space-y-5">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider">Your details</p>
                    <p className="text-sm text-muted-foreground">Shared info, then details for each platform</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>First name *</Label>
                      <Input value={form.firstName} onChange={(e) => update({ firstName: e.target.value })} placeholder="First name" data-testid="sponsor-firstname" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Last name *</Label>
                      <Input value={form.lastName} onChange={(e) => update({ lastName: e.target.value })} placeholder="Last name" data-testid="sponsor-lastname" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>Social / profile links *</Label>
                      {form.socialLinks.length < 8 && (
                        <button type="button" onClick={addSocialLink} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="sponsor-add-social">
                          <Plus className="h-3.5 w-3.5" /> Add
                        </button>
                      )}
                    </div>
                    {form.socialLinks.map((link, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Select value={link.platform} onValueChange={(v) => setSocialLink(i, { platform: v })}>
                          <SelectTrigger className="h-10 w-[128px] shrink-0 bg-background text-sm" data-testid={`sponsor-social-platform-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[hsl(240_8%_10%)] text-white border-[hsl(240_6%_17%)]">
                            {SOCIAL_PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input
                          value={link.handle}
                          onChange={(e) => setSocialLink(i, { handle: e.target.value })}
                          placeholder="Enter your profile handle link"
                          data-testid={`sponsor-social-${i}`}
                        />
                        {form.socialLinks.length > 1 && (
                          <button type="button" onClick={() => removeSocialLink(i)} className="text-muted-foreground hover:text-foreground" data-testid={`sponsor-social-remove-${i}`}>
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label>Sponsorship proposal <span className="font-normal text-muted-foreground">(optional)</span></Label>
                      {form.proposalFiles.length < MAX_PROPOSAL_FILES && (
                        <label className="flex cursor-pointer items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="sponsor-proposal-add">
                          <Plus className="h-3.5 w-3.5" /> Upload file
                          <input type="file" accept={PROPOSAL_ACCEPT} multiple className="hidden" onChange={pickProposal} />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Already have a sponsorship proposal you&apos;d like to include? Upload it here — it&apos;s not required. PDF, DOC, or images.
                    </p>
                    {form.proposalFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2" data-testid={`sponsor-proposal-${i}`}>
                        <FileText className="h-4 w-4 shrink-0 text-primary" />
                        <span className="flex-1 truncate text-sm">{f.fileName}</span>
                        <Select value={f.label} onValueChange={(v) => setProposalLabel(i, v)}>
                          <SelectTrigger className="h-8 w-[150px] shrink-0 bg-background text-xs" data-testid={`sponsor-proposal-label-${i}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[hsl(240_8%_10%)] text-white border-[hsl(240_6%_17%)]">
                            {PROPOSAL_LABELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button type="button" onClick={() => removeProposal(i)} className="text-muted-foreground hover:text-foreground" data-testid={`sponsor-proposal-remove-${i}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-3 rounded-md border border-border bg-secondary/30 p-4" data-testid="sponsor-rig-section">
                    <div>
                      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
                        <Gamepad2 className="h-4 w-4" /> Your rig / setup
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Tell us what you race on — pre-filled from your profile gear, edit anything here. Pick a brand or choose &quot;Other&quot; to type your own.
                      </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {RIG_FIELDS.map((rf) => (
                        <RigField
                          key={rf.key}
                          label={`${rf.label}${rf.required ? " *" : ""}`}
                          category={rf.category}
                          manufacturers={manufacturers}
                          value={form.rigEquipment[rf.key] || ""}
                          optional={!rf.required}
                          onChange={(v) => update({ rigEquipment: { ...form.rigEquipment, [rf.key]: v } })}
                        />
                      ))}
                    </div>
                  </div>
                  {form.selectedPlatforms.map((id) => {
                    const d = form.platformData[id] || emptyPlatformData;
                    return (
                      <div key={id} className="space-y-3 rounded-md border border-border bg-secondary/30 p-4" data-testid={`sponsor-details-platform-${id}`}>
                        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
                          <Gamepad2 className="h-4 w-4" /> {platformLabel(id)}
                        </p>
                        <div className="space-y-1.5">
                          <Label>Skill / Safety Rating *</Label>
                          <Input value={d.licenseLevel} onChange={(e) => updatePlatformData(id, { licenseLevel: e.target.value })} placeholder="e.g., iRating 2500 / A 4.99 Safety" data-testid={`sponsor-license-${id}`} />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Username / profile name *</Label>
                          <Input value={d.username} onChange={(e) => updatePlatformData(id, { username: e.target.value })} placeholder="Your in-game / platform username" data-testid={`sponsor-username-${id}`} />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Driver profile screenshot *</Label>
                          {d.profileScreenshot ? (
                            <div>
                              <img src={d.profileScreenshot} alt="" className="w-full rounded-md object-cover" />
                              <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-secondary/40 py-2 text-sm text-primary hover:text-foreground" data-testid={`sponsor-screenshot-change-${id}`}>
                                <Camera className="h-4 w-4" /> Change screenshot
                                <input type="file" accept="image/*" className="hidden" onChange={pickScreenshot(id)} />
                              </label>
                            </div>
                          ) : (
                            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 p-5 hover:border-primary" data-testid={`sponsor-screenshot-upload-${id}`}>
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15">
                                <Upload className="h-4 w-4 text-primary" />
                              </div>
                              <p className="text-sm font-semibold">Upload profile screenshot</p>
                              <p className="text-xs text-muted-foreground">Screenshot of your driver profile on this platform</p>
                              <input type="file" accept="image/*" className="hidden" onChange={pickScreenshot(id)} />
                            </label>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-1.5"><Trophy className="h-3.5 w-3.5 text-primary" /> Wins / Notable Tournaments</Label>
                            <button type="button" onClick={() => addWin(id)} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`sponsor-add-win-${id}`}>
                              <Plus className="h-3.5 w-3.5" /> Add
                            </button>
                          </div>
                          {(d.wins || []).map((w, wi) => (
                            <div key={wi} className="flex items-center gap-2">
                              <Input value={w.name} onChange={(e) => setWin(id, wi, { name: e.target.value })} placeholder="Tournament / league name" data-testid={`sponsor-win-name-${id}-${wi}`} />
                              <Input value={w.position} onChange={(e) => setWin(id, wi, { position: e.target.value })} placeholder="Finish (e.g. 1st)" className="max-w-[38%]" data-testid={`sponsor-win-pos-${id}-${wi}`} />
                              {(d.wins || []).length > 1 && (
                                <button type="button" onClick={() => removeWin(id, wi)} className="text-muted-foreground hover:text-foreground" data-testid={`sponsor-win-remove-${id}-${wi}`}>
                                  <X className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        <div className="space-y-1.5">
                          <Label>Upcoming Tournaments / Leagues *</Label>
                          <Input value={d.tournaments} onChange={(e) => updatePlatformData(id, { tournaments: e.target.value })} placeholder="Tournaments you will compete in" data-testid={`sponsor-tournaments-${id}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Step 2 — Livery (per-platform) */}
              {step === 2 && (
                <div className="space-y-5">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider">Livery</p>
                    <p className="text-sm text-muted-foreground">Upload a livery or describe your plans for each platform</p>
                  </div>
                  {form.selectedPlatforms.map((id) => {
                    const d = form.platformData[id] || emptyPlatformData;
                    return (
                      <div key={id} className="space-y-3 rounded-md border border-border bg-secondary/30 p-4" data-testid={`sponsor-livery-platform-${id}`}>
                        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
                          <Gamepad2 className="h-4 w-4" /> {platformLabel(id)}
                        </p>
                        {d.liveryImage ? (
                          <div className="space-y-2">
                            {isImageData(d.liveryImage) ? (
                              <img src={d.liveryImage} alt="" className="w-full rounded-md object-cover" />
                            ) : (
                              <a
                                href={d.liveryImage}
                                download={d.liveryFileName || "livery"}
                                className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2.5 text-sm text-primary hover:text-foreground"
                                data-testid={`sponsor-livery-file-${id}`}
                              >
                                <FileText className="h-4 w-4 shrink-0" />
                                <span className="flex-1 truncate">{d.liveryFileName || "Livery file"}</span>
                                <Download className="h-4 w-4 shrink-0" />
                              </a>
                            )}
                            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-border bg-secondary/40 py-2 text-sm text-primary hover:text-foreground" data-testid={`sponsor-livery-change-${id}`}>
                              <Camera className="h-4 w-4" /> Change file
                              <input type="file" accept={LIVERY_ACCEPT} className="hidden" onChange={pickLivery(id)} />
                            </label>
                          </div>
                        ) : (
                          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border border-dashed border-border bg-secondary/40 p-6 hover:border-primary" data-testid={`sponsor-livery-upload-${id}`}>
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/15">
                              <Upload className="h-5 w-5 text-primary" />
                            </div>
                            <p className="text-sm font-semibold">Upload livery file</p>
                            <p className="text-xs text-muted-foreground">PNG, JPG, WEBP, GIF, BMP, TGA & TradingPaints files</p>
                            <input type="file" accept={LIVERY_ACCEPT} className="hidden" onChange={pickLivery(id)} />
                          </label>
                        )}
                        <div className="space-y-1.5">
                          <Label>
                            Tell us about your plans for your liveries and social/streaming branding {!d.liveryImage && <span className="text-primary">*</span>}
                          </Label>
                          <Textarea
                            rows={4}
                            value={d.liveryDescription}
                            onChange={(e) => updatePlatformData(id, { liveryDescription: e.target.value })}
                            placeholder="Tell us how you plan to modify your livery to include any sponsor(s) you are approved for including placement and size. No livery yet? No problem, tell us what you are planning now and how you'll incorporate the sponsors."
                            data-testid={`sponsor-livery-desc-${id}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Step 3 — Review */}
              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider">Review &amp; submit</p>
                    <p className="text-sm text-muted-foreground">Confirm your application details</p>
                  </div>
                  <div className="divide-y divide-border rounded-md border border-border bg-secondary/40">
                    {[
                      ["Name", `${form.firstName} ${form.lastName}`.trim() || "—"],
                      ["Platforms", form.selectedPlatforms.map(platformLabel).join(", ") || "—"],
                      ["Social links", `${form.socialLinks.filter((l) => l.handle.trim()).length} link(s)`],
                      ["Proposal files", form.proposalFiles.length ? `${form.proposalFiles.length} file(s)` : "None"],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="max-w-[60%] text-right font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                  {form.selectedPlatforms.map((id) => {
                    const d = form.platformData[id] || emptyPlatformData;
                    return (
                      <div key={id} className="rounded-md border border-border bg-secondary/30 p-3 text-sm" data-testid={`sponsor-review-platform-${id}`}>
                        <p className="font-semibold text-primary">{platformLabel(id)}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Level: {d.licenseLevel || "—"} · Upcoming: {d.tournaments || "—"}
                          {(d.wins || []).filter((w) => (w.name || "").trim()).length ? ` · ${(d.wins || []).filter((w) => (w.name || "").trim()).length} win(s)` : ""}
                          {" · "}Livery: {d.liveryImage ? (d.liveryFileName || "File uploaded") : d.liveryDescription ? "Description provided" : "Not provided"}
                        </p>
                      </div>
                    );
                  })}
                  <div className="space-y-1.5">
                    <Label>Additional info</Label>
                    <Textarea rows={3} value={form.additionalInfo} onChange={(e) => update({ additionalInfo: e.target.value })} placeholder="Anything else you'd like us to know..." data-testid="sponsor-additional" />
                  </div>
                  <p className="text-center text-xs text-muted-foreground">
                    By submitting, you agree to display Pit Lane Sim Racing branding and represent the team professionally.
                  </p>
                </div>
              )}

              <div className="mt-6 flex items-center justify-between gap-2 border-t border-border pt-4">
                <Button variant="outline" onClick={step === 0 ? undefined : back} disabled={step === 0} data-testid="sponsor-back">
                  <ArrowLeft className="mr-1 h-4 w-4" /> Back
                </Button>
                {step < STEPS.length - 1 ? (
                  <Button onClick={next} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sponsor-next">
                    Continue <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <Button onClick={submit} disabled={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sponsor-submit">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
                  </Button>
                )}
              </div>
            </>
          )}
        </Card>

        <ApplicationsPanel requests={requests} loading={loading} active={active} onOpen={openThread} />
      </div>

      {/* Messaging thread */}
      {active && (
        <Card className="border-border bg-card p-6" data-testid="sponsor-thread">
          <h3 className="mb-1 font-display text-lg font-semibold">Messages with the sponsorship team</h3>
          <p className="mb-3 text-xs text-muted-foreground">Re: {active.real_name}&apos;s application · {(active.status || "").toUpperCase()}</p>
          <div className="mb-3 max-h-80 space-y-2 overflow-y-auto rounded-md border border-border bg-secondary/30 p-3">
            {messages.length === 0 ? (
              <p className="text-sm text-muted-foreground">No messages yet — start the conversation.</p>
            ) : (
              messages.map((m) => (
                <div key={m.id} className="rounded-md bg-card p-2 text-sm">
                  <p className="text-xs font-semibold">{m.sender_username}</p>
                  <p className="mt-0.5 text-foreground/90">{m.content}</p>
                </div>
              ))
            )}
          </div>
          <div className="flex gap-2">
            <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Send a message to the sponsorship team..." onKeyDown={(e) => e.key === "Enter" && sendMsg()} data-testid="sponsor-msg-input" />
            <Button onClick={sendMsg} disabled={!msg.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="sponsor-msg-send">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
